import { useCallback, useEffect, useState } from "react";

let deferredPrompt = null;
const listeners = new Set();
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferredPrompt = e; listeners.forEach((fn) => fn()); });
  window.addEventListener("appinstalled", () => { deferredPrompt = null; localStorage.setItem("kt_pwa_installed", "1"); listeners.forEach((fn) => fn()); });
}

export const isStandalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
export const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

export function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return;
  window.addEventListener("load", () => { navigator.serviceWorker.register("/sw.js").catch(() => {}); });
}

export function useInstallPrompt() {
  const [, tick] = useState(0);
  useEffect(() => { const fn = () => tick((n) => n + 1); listeners.add(fn); return () => listeners.delete(fn); }, []);
  const install = useCallback(async () => {
    if (!deferredPrompt) return false;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") deferredPrompt = null;
    tick((n) => n + 1);
    return outcome === "accepted";
  }, []);
  const standalone = isStandalone();
  return { canInstall: !!deferredPrompt && !standalone, install, installed: standalone || localStorage.getItem("kt_pwa_installed") === "1", ios: isIOS() && !standalone, standalone };
}

export function useWakeLock(active) {
  const [held, setHeld] = useState(false);
  useEffect(() => {
    if (!active || !("wakeLock" in navigator)) { setHeld(false); return undefined; }
    let lock = null, alive = true;
    const acquire = () => navigator.wakeLock.request("screen").then((l) => { if (!alive) return l.release(); lock = l; setHeld(true); l.addEventListener("release", () => setHeld(false)); }).catch(() => setHeld(false));
    acquire();
    const onVis = () => { if (document.visibilityState === "visible") acquire(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { alive = false; document.removeEventListener("visibilitychange", onVis); lock?.release?.(); };
  }, [active]);
  return { held, supported: typeof navigator !== "undefined" && "wakeLock" in navigator };
}

export function useFullscreen() {
  const [on, setOn] = useState(() => !!document.fullscreenElement);
  useEffect(() => { const fn = () => setOn(!!document.fullscreenElement); document.addEventListener("fullscreenchange", fn); return () => document.removeEventListener("fullscreenchange", fn); }, []);
  const toggle = useCallback(() => { if (document.fullscreenElement) document.exitFullscreen?.(); else document.documentElement.requestFullscreen?.().catch(() => {}); }, []);
  return { on, toggle, supported: !!document.documentElement.requestFullscreen };
}
